import { styled } from '@mui/material/styles';
import {
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  Button,
  Typography,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { useCommonTranslation } from '../../../hooks/useCommonTranslation';
import OverrideForm from './OverrideForm';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { useLookups } from '../../../hooks/useLookups';
import * as lookupSlice from '../../../data/lookup.slice';
import { ServicePlan } from '../../../data/models/service-plan.model';

const PREFIX = 'OverrideDialog';

const classes = {
  content: `${PREFIX}-content`,
  message: `${PREFIX}-message`,
};

const RootDialog = styled(Dialog)(({ theme }) => ({
  [`& .${classes.content}`]: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },

  [`& .${classes.message}`]: {
    marginBottom: theme.spacing(2),
  },
}));

interface OverrideDialogProps {
  open: boolean;
  servicePlans: ServicePlan[];
  close: (data: any | null) => void;
}

export default function OverrideDialog({
  open,
  servicePlans,
  close,
}: OverrideDialogProps) {
  const t = useCommonTranslation();

  useLookups(['tripLegOverrideReason']);
  const overrideReasons = useAppSelector(
    lookupSlice.selectOverrideAuthorizationReasons,
  );

  const formHook = useForm({
    defaultValues: {
      OverrideFirstName: '',
      OverrideLastName: '',
      OverrideReason: '',
      ServicePlan: '',
      AllowOverride: false,
    },
  });

  const isOverrideChecked = formHook.watch('AllowOverride');

  const handleSave = (data: any) => {
    formHook.reset();
    close(data);
  };

  const handleClose = () => {
    formHook.reset();
    close(null);
  };

  return (
    <RootDialog open={open} onClose={handleClose}>
      <DialogTitle>{t('reservation.requestOverride')}</DialogTitle>
      <DialogContent className={classes.content}>
        <Typography className={classes.message}>
          {t('reservation.overrideMessage')}
        </Typography>
        <OverrideForm
          overrideReasons={overrideReasons}
          servicePlans={servicePlans}
          formHook={formHook}
        />
      </DialogContent>
      <DialogActions>
        <Button variant="contained" color="secondary" onClick={handleClose}>
          {t('common.close')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={!formHook.formState.isValid || !isOverrideChecked}
          onClick={formHook.handleSubmit(handleSave)}
          data-fs-element="CreateReservationRequestOverrideSubmitButton">
          {t('common.done')}
        </Button>
      </DialogActions>
    </RootDialog>
  );
}
